import type { QueryClient } from '@tanstack/react-query'
import type { ManagedUser } from '@/api/usersApi'

/**
 * The Users table's cached pages, patched in place for a toggle.
 *
 * A switch on that table (Super admin, Enabled, SSO Only) reads its state from
 * the cached list. Waiting for the refetch after a save left the switch showing
 * the OLD value for as long as the round trip took — invisible locally, a
 * visible pause on a deployed environment, where the first click looked like it
 * did nothing and a second click sent the same change again. Patching the cache
 * makes the switch move on the click; the server's own copy then replaces it.
 *
 * Tolerant of any cache entry under the `['users']` prefix: only the paged list
 * shape (`{ items, total }`) is touched, everything else is returned unchanged.
 */
type UsersPage = { items: ManagedUser[]; total: number }

function isUsersPage(data: unknown): data is UsersPage {
  return !!data && typeof data === 'object' && Array.isArray((data as UsersPage).items)
}

/** Apply `patch` to the user with `id`, on the click (optimistic). */
export function patchUserInPage<T>(data: T, id: string, patch: Partial<ManagedUser>): T {
  if (!isUsersPage(data)) return data
  return { ...data, items: data.items.map((u) => (u.id === id ? { ...u, ...patch } : u)) }
}

/** Replace the user with the server's copy, once the save has answered. */
export function replaceUserInPage<T>(data: T, user: ManagedUser): T {
  if (!isUsersPage(data)) return data
  return { ...data, items: data.items.map((u) => (u.id === user.id ? user : u)) }
}

type Snapshot = { previous: [readonly unknown[], unknown][] }

/**
 * Mutation options for a per-user switch on the Users table: the cached row is
 * patched on the click, replaced by the server's copy when the save answers,
 * restored if it fails, and the list refetches in the background either way.
 * Plain options rather than a hook so the timing can be tested without a DOM.
 */
export function userToggleOptions<V extends { id: string }>(
  qc: QueryClient,
  send: (v: V) => Promise<ManagedUser>,
  patchOf: (v: V) => Partial<ManagedUser>,
  onErr: (e: unknown) => void,
) {
  return {
    mutationFn: send,
    onMutate: async (v: V): Promise<Snapshot> => {
      await qc.cancelQueries({ queryKey: ['users'] })
      const previous = qc.getQueriesData({ queryKey: ['users'] })
      qc.setQueriesData({ queryKey: ['users'] }, (d: unknown) => patchUserInPage(d, v.id, patchOf(v)))
      return { previous }
    },
    onSuccess: (saved: ManagedUser) => {
      qc.setQueriesData({ queryKey: ['users'] }, (d: unknown) => replaceUserInPage(d, saved))
    },
    onError: (e: unknown, _v: V, ctx: Snapshot | undefined) => {
      for (const [key, data] of ctx?.previous ?? []) qc.setQueryData(key, data)
      onErr(e)
    },
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: ['users'], exact: false })
    },
  }
}
